import Router from 'next/router'
import axios from 'axios'
import { dataSources } from '../utils/data-sources'

const constantItems = {
  overlayBackground: '#overlayBG'
}

let instance = null

export function getHttpClient () {
  return axios.create()
}

export default class Http {
  constructor (sessionStore) {
    if (!instance) {
      instance = this
    }

    this.sessionStore = sessionStore
    this.authRefreshInterceptor = null
    this.authRequestInterceptor = null
    this.dataSources = dataSources()
    this.axiosInstance = getHttpClient()
    this._createAuthRefreshInterceptor()
    this._createAuthRequestInterceptor()

    return instance
  }

  static getInstance (sessionStore) {
    return new Http(sessionStore)
  }

  _expireSession () {
    this.sessionStore.destroySessionWithMessage('Your session has expired. Please log in again.')
  }

  _unauthorised (e) {
    if (document.querySelector(constantItems.overlayBackground)) {
      this._expireSession()
      return e.response
    } else {
      Router.push('/login')
      return e.response
    }
  }

  _error () {
    Router.push('/error')
  }

  intercept (error) {
    console.log('intercept', error)
    if (error.response.status === 401) {
      return this._unauthorised(error)
    } else if (error.response.status === 403) {
      return this._unauthorised(error)
    } else {
      return error.response
    }
  }

  _createAuthRequestInterceptor () {
    this.authRequestInterceptor = this.axiosInstance.interceptors.request.use(config => {
      if (this.sessionStore.session) {
        config.headers['Authorization'] = `Bearer ${this.sessionStore.session.access_token}`
      }
      if (this.sessionStore.sysAdmin && this.sessionStore.sysAdminTenant) {
        config.headers['TenantName'] = `${this.sessionStore.sysAdminTenant.name}`
        config.headers['TenantSchema'] = `${this.sessionStore.sysAdminTenant.schema}`
      }
      return config
    }, error => {
      return Promise.reject(error)
    })
  }

  _createAuthRefreshInterceptor () {
    this.authRefreshInterceptor = this.axiosInstance.interceptors.response.use(
      response => {
        return response
      },
      error => {
        const errorResponse = error.response
        if (errorResponse.status === 401 && (this.sessionStore.isLoggedIn || this.sessionStore.isAdminLoggedIn)) {
          const refreshObject = {
            url: this.dataSources.adminAuthRefresh,
            body: {
              'refresh_token': this.sessionStore.session.refresh_token
            }
          }
          if (this.sessionStore.isLoggedIn) {
            refreshObject.url = this.dataSources.userAuthRefresh
            refreshObject.body.realm = this.sessionStore.tenant
          }
          this.axiosInstance.interceptors.response.eject(this.authRefreshInterceptor)
          return getHttpClient().post(refreshObject.url, refreshObject.body).then(response => {
            this.sessionStore.setSession(response.data)
            errorResponse.config.headers['Authorization'] = 'Bearer ' + response.data.access_token
            this._createAuthRefreshInterceptor()
            return this.axiosInstance(errorResponse.config)
          }).catch(error => {
            console.log('catch method refresh token', error)
            if (error.response.status === 401 && (this.sessionStore.isLoggedIn || this.sessionStore.isAdminLoggedIn)) {
              if (document.querySelector(constantItems.overlayBackground)) {
                this._expireSession()
                return Promise.reject(error)
              } else {
                return Promise.reject(error)
              }
            }
          })
        } else if (errorResponse.status === 403 || (errorResponse.status === 401 && !this.sessionStore.isLoggedIn)) {
          if (document.querySelector(constantItems.overlayBackground)) {
            this._expireSession()
            return Promise.reject(error)
          } else {
            return Promise.reject(error)
          }
        }
        return Promise.reject(error)
      }
    )
  }

  _getOptions (opts) {
    return Object.assign({
      headers: {
        'Content-Type': 'application/json'
      }
    }, opts)
  }

  _cleanUrl (url) {
    if (/\/static.*\.json/.test(url)) {
      return url.substring(0, url.lastIndexOf('.json') + 5)
    }
    return url
  }

  refreshToken () {
    return getHttpClient().post(this.dataSources.userAuthRefresh, {
      'realm': this.sessionStore.tenant,
      'refresh_token': this.sessionStore.session.refresh_token
    }).then(response => {
      this.sessionStore.setSession(response.data)
      return response.data
    }).catch(error => {
      if (error.response.status === 401 && this.sessionStore.isLoggedIn) {
        if (document.querySelector(constantItems.overlayBackground)) {
          this._expireSession()
          return Promise.reject(error)
        } else {
          return Promise.reject(error)
        }
      }
    })
  }

  getData (url, options) {
    return this.axiosInstance
      .get(this._cleanUrl(url), this._getOptions(options))
      .then((response) => {
        return response.data
      }).catch((e) => {
        return this.intercept(e)
      })
  }

  getFile (url, options) {
    return this.axiosInstance
      .get(this._cleanUrl(url), this._getOptions({ ...options, 'responseType': 'blob' }))
      .catch((e) => {
        return this.intercept(e)
      })
  }

  putData (url, data, options) {
    return this.axiosInstance
      .put(this._cleanUrl(url), data, this._getOptions(options))
      .then((response) => {
        return response.data
      }).catch((e) => {
        return this.intercept(e)
      })
  }

  postData (url, data, options) {
    return this.axiosInstance
      .post(this._cleanUrl(url), data, this._getOptions(options))
      .then((response) => {
        return response.data
      }).catch((e) => {
        return this.intercept(e)
      })
  }

  deleteData (url, options) {
    return this.axiosInstance
      .delete(this._cleanUrl(url), this._getOptions(options))
      .then((response) => {
        return response.data
      }).catch((e) => {
        return this.intercept(e)
      })
  }
}
